import type { TFunction } from "i18next";
import type { RegionPanel, ShopifySalesCrossFilterRequest } from "src/services/ShopifySalesProductAPI";
import { toArabicIndicDigitsInString } from "src/utils/numberFormat";
import { formatDashboardValueString } from "src/utils/formatDashboardValue";
import { CHART_COLOR_PALETTE } from "src/components/charts/ChartPalette";
import type { DefinitionTooltipInfo } from "src/components/common/DefinitionTooltip";
import type { ShopifyMetric, ShopifyRegion, SparkPoint } from "../ShopifyGeoSpatialDashboard.data";

export interface SparkChartPoint {
  x: number;
  y: number;
}

export interface HoverState {
  index: number;
  period: "current" | "previous";
  clientX: number;
  clientY: number;
}

export interface HeatmapHover {
  clientX: number;
  clientY: number;
  hour: string;
  day: string;
  orders: number;
}

export interface DonutHover {
  clientX: number;
  clientY: number;
  name: string;
  value: number;
  formattedValue?: string;
}

export type GeoCrossFilter = ShopifySalesCrossFilterRequest | null;
export type PakistanShapeKey = "punjab" | "sindh" | "balochistan" | "kpk" | "ajk";

/** API panels may omit `id`; infer slug for MapLibre keys, React keys, and `filter_region`. */
export function inferPakistanRegionSlug(
  rawId: string | undefined | null,
  title: string | undefined | null,
): string {
  const id = String(rawId ?? "")
    .trim()
    .toLowerCase();
  const tl = String(title ?? "").toLowerCase();
  const lookup = `${id} ${tl}`;

  if (
    !lookup.trim() ||
    lookup.includes("others") ||
    lookup.includes("other") ||
    lookup.includes("unknown") ||
    lookup.includes("unassigned") ||
    lookup.includes("uncategorized") ||
    lookup.includes("unmapped") ||
    lookup.includes("undefined") ||
    lookup.includes(" null ") ||
    lookup.trim() === "null" ||
    lookup.includes("n/a") ||
    lookup.includes("not set") ||
    lookup.includes("no region") ||
    lookup.includes("no_region") ||
    lookup.includes("no-region") ||
    lookup.includes("no location") ||
    lookup.includes("no_location") ||
    lookup.includes("no-location")
  ) {return "others";}
  if (lookup.includes("punjab")) {return "punjab";}
  if (lookup.includes("sindh")) {return "sindh";}
  if (lookup.includes("baloch")) {return "balochistan";}
  if (lookup.includes("kpk") || lookup.includes("khyber")) {return "kpk";}
  if (lookup.includes("federal") || lookup.includes("islamabad")) {return "federal";}
  return "ajk";
}

export const isPakistanShapeKey = (value: string): value is PakistanShapeKey =>
  value === "punjab" ||
  value === "sindh" ||
  value === "balochistan" ||
  value === "kpk" ||
  value === "ajk";

export const resolveRegionShapeKey = (region: ShopifyRegion): PakistanShapeKey => {
  const slug = inferPakistanRegionSlug(region.id, region.title);
  return isPakistanShapeKey(slug) ? slug : "ajk";
};

export const isOthersRegion = (region: Pick<ShopifyRegion, "id" | "title">): boolean =>
  inferPakistanRegionSlug(region.id, region.title) === "others";

export const hasNonZeroCityValue = (city: RegionPanel["cities"][number]): boolean =>
  Math.abs(Number(city.net_revenue) || 0) +
    Math.abs(Number(city.customers ?? city.customer_count) || 0) +
    Math.abs(Number(city.orders ?? city.order_count) || 0) >
  0;

export const REGION_DISPLAY_ORDER: Record<string, number> = {
  punjab: 0,
  sindh: 1,
  balochistan: 2,
  kpk: 3,
  ajk: 4,
  federal: 5,
  others: 6,
};

export const REGION_SLUG_FALLBACK_TITLE: Record<string, string> = {
  punjab: "Punjab",
  sindh: "Sindh",
  balochistan: "Balochistan",
  kpk: "KPK",
  ajk: "AJK + Gilgit",
  federal: "Federal",
  others: "Others",
};

export const normalizeGeoMetricKey = (value: string) =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");


export const GEO_KPI_TOOLTIP_KEYS: Record<string, string> = {
  netrevenue: "totalSales",
  totalsales: "totalSales",
  revenue: "totalSales",
  orders: "orders",
  netquantity: "netQuantity",
  discount: "discounts",
  discounts: "discounts",
  returns: "returns",
  refunds: "returns",
  returncount: "returnOrders",
};

export const getGeoMetricDefinition = (
  metric: ShopifyMetric,
  scopeLabel: string,
  t: TFunction,
): DefinitionTooltipInfo => {
  const entry =
    GEO_KPI_TOOLTIP_KEYS[normalizeGeoMetricKey(metric.key)] ??
    GEO_KPI_TOOLTIP_KEYS[normalizeGeoMetricKey(metric.title)] ??
    "totalSales";
  const scope = scopeLabel.trim() || t("shopifyGeo.tooltips.scopeFallback");
  const formula = t(`shopifyGeo.kpiTooltips.${entry}.formula`, { scope, defaultValue: "" });

  return {
    description: t(`shopifyGeo.kpiTooltips.${entry}.description`, { scope }),
    ...(formula ? { formula } : {}),
  };
};

export const getGeoMetricDisplayTitle = (metric: ShopifyMetric, t: TFunction) => {
  const normalizedKey = normalizeGeoMetricKey(metric.key);
  const normalizedTitle = normalizeGeoMetricKey(metric.title);
  if (["netrevenue", "revenue", "totalsales"].includes(normalizedKey) || normalizedTitle.includes("netrevenue")) {
    return t("shopifyGeo.kpi.totalSales");
  }
  if (normalizedKey === "discount") {
    return t("shopifyGeo.kpi.discounts");
  }
  if (normalizedKey === "refunds") {
    return t("shopifyGeo.kpi.returns");
  }
  if (normalizedTitle === "returncount" || normalizedKey === "returncount") {
    return t("shopifyGeo.kpi.returnOrders");
  }
  return metric.title;
};

/** Human-readable region for filter chip (panel title when data loaded, else slug fallback). */
export function regionLabelForChip(
  regionSlug: string | undefined | null,
  panels: ShopifyRegion[],
): string {
  const s = String(regionSlug ?? "").trim().toLowerCase();
  if (!s) {
    return "";
  }
  const fromPanel = panels.find(p => inferPakistanRegionSlug(p.id, p.title) === s);
  if (fromPanel) {
    return fromPanel.title;
  }
  return REGION_SLUG_FALLBACK_TITLE[s] ?? s.charAt(0).toUpperCase() + s.slice(1);
}

export function toCityFilterSlug(name: string): string {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

export function applyCityCrossFilter(
  prev: GeoCrossFilter,
  region: string,
  city: string,
): GeoCrossFilter {
  return {
    filter_region: region,
    filter_city: toCityFilterSlug(city),
    filter_hour: prev?.filter_hour,
    filter_weekday: prev?.filter_weekday,
  };
}

export function applyHeatmapCrossFilter(
  prev: GeoCrossFilter,
  region: string,
  hour: number,
  weekday: number,
): GeoCrossFilter {
  return {
    filter_region: region,
    filter_city: prev?.filter_city,
    filter_hour: hour,
    filter_weekday: weekday,
  };
}

export function hasCrossFilterParams(f: GeoCrossFilter): boolean {
  return Boolean(f?.filter_region?.trim());
}

export function formatGeoFilterChipText(
  filter: {
    region?: string | null;
    city?: string | null;
    hour?: number | null;
    weekday?: number | null;
  },
  t: TFunction,
  weekDays: string[],
  hourLabels: string[],
  panels: ShopifyRegion[],
  cityDisplayName?: string | null,
): string {
  const regionSlug = filter.region?.trim();
  if (!regionSlug) {
    return "";
  }
  const region = regionLabelForChip(regionSlug, panels).trim() || "—";
  const city = (cityDisplayName ?? filter.city)?.trim() ?? "";
  const hasCity = Boolean(city);
  const hasHeatmap =
    typeof filter.hour === "number" && typeof filter.weekday === "number";

  if (hasCity && hasHeatmap) {
    const day = weekDays[filter.weekday!] ?? "";
    const hr = hourLabels[filter.hour!] ?? "";
    return t("shopifyGeo.crossFilter.stacked", { region, city, day, hour: hr });
  }
  if (hasCity) {
    return t("shopifyGeo.crossFilter.city", { region, city });
  }
  if (hasHeatmap) {
    const day = weekDays[filter.weekday!] ?? "";
    const hr = hourLabels[filter.hour!] ?? "";
    return t("shopifyGeo.crossFilter.heatmap", { region, day, hour: hr });
  }
  return region;
}

export const normalizeCity = (name: string) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export function titleCaseWords(value: string): string {
  return String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function resolveCityDisplayName(
  citySlug: string | null | undefined,
  cities?: ShopifyRegion["cities"],
): string | null {
  const raw = citySlug?.trim();
  if (!raw) {
    return null;
  }
  const normalized = normalizeCity(raw);
  const slug = toCityFilterSlug(raw);
  const fromList = cities?.find(city => {
    const cityNorm = normalizeCity(city.city);
    return cityNorm === normalized || toCityFilterSlug(city.city) === slug;
  });
  if (fromList?.city?.trim()) {
    return fromList.city.trim();
  }
  return titleCaseWords(raw.replace(/-/g, " "));
}

/** Same palette as the sales-analytics API uses for top_products so colors stay consistent. */
export const TOP_PRODUCT_PALETTE = CHART_COLOR_PALETTE;
export const PRODUCT_BAR_RANK_COLORS = [
  "#2b74e4",
  "#2fc7b5",
  "#a64fee",
  "#5d97ee",
  "#12b886",
] as const;

export function stableColorForProductName(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (Math.imul(31, h) + name.charCodeAt(i)) | 0;
  }
  return TOP_PRODUCT_PALETTE[Math.abs(h) % TOP_PRODUCT_PALETTE.length];
}

export function transformApiRegionToShopifyRegion(panel: RegionPanel, t: TFunction): ShopifyRegion {
  const slug = inferPakistanRegionSlug(panel.id, panel.title);
  const title = slug === "others"
    ? t("shopifyGeo.regions.others", { defaultValue: "Others" })
    : panel.title;
  const heatData = panel.heatmap?.data ?? [];
  const maxHeatmapVal = Math.max(1, ...heatData.flat());

  const heatmapWithLevels = heatData.map(row =>
    row.map(val => {
      if (val === 0) {return 0;}
      return Math.min(4, Math.max(1, Math.ceil((val / maxHeatmapVal) * 4)));
    })
  );

  const metrics: ShopifyMetric[] = (panel.kpis ?? []).map(kpi => ({
    key: kpi.key,
    title: kpi.title,
    value: formatDashboardValueString(kpi.value),
    delta: kpi.delta ?? 0,
    deltaLabel: kpi.delta_label,
    previousDate: kpi.previous_date,
    currentDate: kpi.current_date,
    spark: Array.isArray(kpi.spark) ? kpi.spark : [],
  }));

  return {
    id: slug,
    title,
    cityMapTitle: t("shopifyGeo.panel.cityMapTitle", { region: title }),
    heatmapTitle: t("shopifyGeo.panel.heatmapTitle", { region: title }),
    donutTitle: t("shopifyGeo.panel.donutTitle", { region: title }),
    metrics,
    cities: (panel.cities ?? []).filter(city => hasNonZeroCityValue(city)),
    heatmap: heatmapWithLevels,
    heatmapRaw: heatData,
    products: (panel.top_products ?? []).filter(p => Number(p.value) > 0).map(p => ({
      name: p.name,
      value: p.value,
      valueFormattedValue: p.valueFormattedValue,
      color: stableColorForProductName(p.name),
    })),
  };
}

export const sparkPoints = (
  values: SparkPoint[],
  key: "current" | "previous",
  max: number
): SparkChartPoint[] => {
  const step = values.length > 1 ? 100 / (values.length - 1) : 100;
  return values.map((point, index) => ({
    x: Number((index * step).toFixed(2)),
    y: Number((100 - (point[key] / max) * 100).toFixed(2)),
  }));
};

export const toPolyline = (points: SparkChartPoint[]) => {
  return points.map(point => `${point.x},${point.y}`).join(" ");
};

export const polarToCartesian = (cx: number, cy: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
};

export const describeDonutArc = (
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number
): string => {
  const sweep = endAngle - startAngle;
  // A single SVG elliptical arc cannot span 360° (start and end coincide), so the path
  // collapses and the segment disappears — e.g. when a product filter leaves one top product.
  if (sweep >= 359.99) {
    const mid = startAngle + 180;
    return [
      describeDonutArc(cx, cy, outerRadius, innerRadius, startAngle, mid),
      describeDonutArc(cx, cy, outerRadius, innerRadius, mid, endAngle),
    ].join(" ");
  }

  const outerStart = polarToCartesian(cx, cy, outerRadius, endAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, startAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, endAngle);
  const largeArcFlag = sweep <= 180 ? "0" : "1";

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${innerEnd.x} ${innerEnd.y}`,
    "Z",
  ].join(" ");
};

export const getFixedTooltipPosition = (
  clientX: number,
  clientY: number,
  tooltipWidth: number,
  tooltipHeight = 66,
  horizontalOffset = 12,
  minViewportLeft = 8,
  isRTL = false,
) => {
  const viewportWidth = typeof window === "undefined" ? 1024 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 768 : window.innerHeight;
  const minLeft = isRTL ? 8 : minViewportLeft;
  const maxLeft = Math.max(
    minLeft,
    viewportWidth - tooltipWidth - (isRTL ? minViewportLeft : 8),
  );
  const preferredLeft = isRTL
    ? clientX - horizontalOffset - tooltipWidth
    : clientX + horizontalOffset;
  const left = Math.max(minLeft, Math.min(preferredLeft, maxLeft));
  const top = Math.max(8, Math.min(clientY - tooltipHeight - 10, viewportHeight - tooltipHeight - 8));

  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${tooltipWidth}px`,
    maxWidth: "calc(100vw - 16px)",
    boxSizing: "border-box" as const,
  };
};

export const heatLevelClass = (value: number) => {
  if (value >= 3) {return "shopify-geo__heat-cell--lv4";}
  if (value === 2) {return "shopify-geo__heat-cell--lv3";}
  if (value === 1) {return "shopify-geo__heat-cell--lv2";}
  return "shopify-geo__heat-cell--lv1";
};

export const MAX_VERTEX_MARKERS = 62;

export function formatGeoDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${d.getFullYear()}`;
}


export const geoDigits = (value: string | number, isRTL: boolean) =>
  isRTL ? toArabicIndicDigitsInString(value) : String(value);

export const geoNumberText = (value: string | number, isRTL: boolean) =>
  isRTL ? `\u2066${toArabicIndicDigitsInString(value)}\u2069` : String(value);

export const formatProductBarValue = (value: number, isRTL = false) => {
  const abs = Math.abs(value);
  const digits = abs >= 1000
    ? abs.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : abs.toLocaleString(undefined, { maximumFractionDigits: 1 });
  const signed = value < 0 ? `-${digits}` : digits;
  return isRTL ? `\u2066${toArabicIndicDigitsInString(signed)}\u2069` : signed;
};